import { getObjectLabels, getPipelines, getQuoteTags, getQuoteTypeOptions, getTags } from '@paintscout/util/builder'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import type { OptionsDocument } from 'paintscout'
import ClearIcon from '@material-ui/icons/Clear'
import { makeStyles } from '@material-ui/core'
import React, { useState } from 'react'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import type {
  AddTagsActionConfig,
  AutomationAction,
  AutomationActionType,
  AutomationDocType,
  AutomationOption,
  AutomationTrigger,
  AutomationTriggerType,
  UpdateDealStageActionConfig
} from '@paintscout/util/builder'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  DropdownSelect,
  Collapse,
  Alert
} from '@paintscout/ui'
import { useFlags } from 'shared/components/common/FlagsProvider'

const useStyles = makeStyles<Theme, AutomationOptionDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {},
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2)
    },
    dropdownSelect: {
      marginTop: theme.spacing(2)
    },
    infoAlert: {
      marginTop: theme.spacing(2)
    },
    cancel: {},
    confirm: {}
  }),
  { name: 'AutomationOptionDialog' }
)

export interface AutomationOptionDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  automation: AutomationOption
  options: OptionsDocument
  isNew?: boolean
  onConfirm: (automation: AutomationOption) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
}

function AutomationOptionDialog(props: AutomationOptionDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, automation, isNew, options, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const { productionFlag } = useFlags()
  const objectLabels = getObjectLabels({ options })
  const pipelines = getPipelines({ options }).filter((pipeline) =>
    productionFlag ? true : pipeline.key !== 'production-pipeline'
  )
  console.log({ productionFlag })
  const [currentAutomation, setCurrentAutomation] = useState<AutomationOption>(automation)
  const automationTriggers = [
    { label: 'Estimate status changed', value: 'quote-status-changed' },
    { label: 'Invoice status changed', value: 'invoice-status-changed' }
  ]
  // const automationActions = [
  //   { label: 'Add tag(s)', value: 'add-tags' },
  //   { label: 'Update Deal Stage', value: 'update-deal-stage' }
  // ]

  const estimateStatusOptions = [
    { label: 'Draft', value: 'draft' },
    { label: 'Sent', value: 'sent' },
    { label: 'On Hold', value: 'onHold' },
    { label: 'Viewed', value: 'viewed' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Declined', value: 'declined' }
  ]
  const invoiceStatusOptions = [
    { label: 'Draft', value: 'draft' },
    { label: 'Sent', value: 'sent' },
    { label: 'Viewed', value: 'viewed' },
    { label: 'Partial', value: 'partial' },
    { label: 'Paid', value: 'paid' }
  ]
  const pipelineOptions = pipelines.map((pipeline) => ({
    label: pipeline.label,
    value: pipeline.key
  }))
  const selectedPipeline =
    pipelines.length === 1
      ? pipelineOptions[0]
      : pipelineOptions.find(
          (pipeline) =>
            pipeline.value ===
            (currentAutomation?.actions[0]?.config as UpdateDealStageActionConfig)?.targetPipeline?.value
        )
  const pipelineStageOptions = selectedPipeline
    ? pipelines
        .find((pipeline) => pipeline.key === selectedPipeline.value)
        ?.stages.map((stage) => ({
          label: stage.label,
          value: stage.key
        })) ?? []
    : []

  const quoteTypeOptions = getQuoteTypeOptions({ options, inactive: false })
  const quoteTypesOptions = quoteTypeOptions
    ?.map((quoteTypeOption) => {
      return {
        label: quoteTypeOption.label,
        value: quoteTypeOption.key
      }
    })
    .filter(Boolean)

  const tagOptions = getQuoteTags({ options })

  const automationDocOptions = [
    {
      value: 'quote',
      label: objectLabels.quote.value
    },
    {
      value: 'deal',
      label: 'Deal'
    },
    {
      value: 'contact',
      label: 'Contact'
    },
    {
      value: 'company',
      label: 'Company'
    }
  ]

  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )
  console.log({ currentAutomation })
  function getDefaultTriggerConfig(value: AutomationTriggerType): AutomationTrigger {
    switch (value) {
      case 'quote-status-changed':
        return {
          type: value,
          config: {
            targetStatus: { value: '' },
            quoteTypes: { value: [] },
            output: 'quote'
          }
        }
      case 'invoice-status-changed':
        return {
          type: value,
          config: {
            targetStatus: { value: '' },
            quoteTypes: { value: [] },
            output: 'quote'
          }
        }
    }
  }

  const objectName =
    currentAutomation?.trigger?.type === 'quote-status-changed' ? objectLabels.quote.value.toLowerCase() : 'invoice'

  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2" weight={'normal'}>
          {isNew ? 'Create New Automation' : 'Edit Automation'}
        </Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <Typography variant="h4" weight={'bold'}>
          Start the automation when...
        </Typography>
        <DropdownSelect
          classes={{ root: classes.dropdownSelect }}
          label="Select Trigger"
          required
          options={automationTriggers}
          value={automationTriggers.find((trigger) => trigger.value === currentAutomation?.trigger?.type)}
          onChange={(value) => {
            setCurrentAutomation((prev) => ({
              ...prev,
              trigger: getDefaultTriggerConfig(value.value)
            }))
          }}
          variant="single"
        />
        {currentAutomation.trigger && (
          <>
            {['quote-status-changed', 'invoice-status-changed'].includes(currentAutomation.trigger.type) && (
              <>
                <DropdownSelect
                  classes={{ root: classes.dropdownSelect }}
                  label={`Select ${objectName} status`}
                  required
                  options={
                    currentAutomation.trigger.type === 'quote-status-changed'
                      ? estimateStatusOptions
                      : invoiceStatusOptions
                  }
                  value={(currentAutomation.trigger.type === 'quote-status-changed'
                    ? estimateStatusOptions
                    : invoiceStatusOptions
                  ).find((trigger) => trigger.value === currentAutomation?.trigger.config.targetStatus.value)}
                  onChange={(value) => {
                    setCurrentAutomation((prev) => ({
                      ...prev,
                      trigger: {
                        ...prev.trigger,
                        config: {
                          ...prev.trigger.config,
                          targetStatus: { value: value.value }
                        }
                      }
                    }))
                  }}
                  variant="single"
                />
                <Collapse show={currentAutomation?.trigger.config.targetStatus.value === 'viewed'}>
                  <Alert severity="info" className={classes.infoAlert}>
                    Triggers only on the initial view of the {objectName}, not on repeated views.
                  </Alert>
                </Collapse>
                <DropdownSelect
                  classes={{ root: classes.dropdownSelect }}
                  label={`Select ${currentAutomation.trigger.type === 'quote-status-changed' ? objectLabels.quote.value.toLowerCase() : 'invoice'} type(s)`}
                  required
                  autoApply={false}
                  options={quoteTypesOptions}
                  value={quoteTypesOptions.filter((trigger) =>
                    currentAutomation?.trigger.config.quoteTypes.value.includes(trigger.value as AutomationDocType)
                  )}
                  onChange={(value) => {
                    setCurrentAutomation((prev) => ({
                      ...prev,
                      trigger: {
                        ...prev.trigger,
                        config: {
                          ...prev.trigger.config,
                          quoteTypes: { value: value.map((v) => v.value) }
                        }
                      }
                    }))
                  }}
                  variant="multi"
                />
              </>
            )}
          </>
        )}
        <Typography variant="h4" weight={'bold'} className={classes.dropdownSelect}>
          Then, do the following...
        </Typography>
        {/* <DropdownSelect
          classes={{ root: classes.dropdownSelect }}
          label="Select Action"
          required
          options={automationActions}
          value={automationActions.find((action) => action.value === currentAutomation?.actions[0]?.type)}
          onChange={(value) => {
            setCurrentAutomation((prev) => ({
              ...prev,
              actions: [getDefaultActionConfig(value.value)]
            }))
          }}
          variant="single"
        /> */}
        {currentAutomation.actions.length > 0 && (
          <>
            {['update-deal-stage'].includes(currentAutomation.actions[0].type) && (
              <>
                {pipelineOptions.length > 1 && (
                  <DropdownSelect
                    classes={{ root: classes.dropdownSelect }}
                    label={`Select pipeline`}
                    required
                    options={pipelineOptions}
                    value={pipelineOptions.find(
                      (pipeline) =>
                        pipeline.value ===
                        (currentAutomation.actions[0].config as UpdateDealStageActionConfig).targetPipeline.value
                    )}
                    onChange={(value) => {
                      console.log({ value })
                      setCurrentAutomation((prev) => ({
                        ...prev,
                        actions: [
                          {
                            ...prev.actions[0],
                            config: {
                              ...prev.actions[0].config,
                              targetPipeline: { value: value.value },
                              targetStage: { value: '' }
                            }
                          }
                        ]
                      }))
                    }}
                    variant="single"
                  />
                )}
                <DropdownSelect
                  classes={{ root: classes.dropdownSelect }}
                  label={`Move deal to new stage`}
                  required
                  options={pipelineStageOptions}
                  value={pipelineStageOptions.find(
                    (stage) =>
                      stage.value ===
                      (currentAutomation.actions[0].config as UpdateDealStageActionConfig).targetStage.value
                  )}
                  onChange={(value) => {
                    console.log({ value })
                    setCurrentAutomation((prev) => ({
                      ...prev,
                      actions: [
                        {
                          ...prev.actions[0],
                          config: {
                            ...prev.actions[0].config,
                            targetStage: { value: value.value }
                          }
                        }
                      ]
                    }))
                  }}
                  variant="single"
                />
              </>
            )}
            {['add-tags'].includes(currentAutomation.actions[0].type) && (
              <>
                <DropdownSelect
                  classes={{ root: classes.dropdownSelect }}
                  label={`Tag(s)`}
                  required
                  options={tagOptions.map((tag) => ({
                    label: tag.label,
                    value: tag.name
                  }))}
                  value={getTags({
                    options,
                    keys: (currentAutomation.actions[0].config as AddTagsActionConfig).tags.value as string[]
                  }).map((tag) => ({
                    label: tag.label,
                    value: tag.name
                  }))}
                  onChange={(value) => {
                    setCurrentAutomation((prev) => ({
                      ...prev,
                      actions: [
                        {
                          ...prev.actions[0],
                          config: {
                            ...prev.actions[0].config,
                            tags: { value: value.map((v) => v.value) }
                          }
                        }
                      ]
                    }))
                  }}
                  variant="multi"
                />
                <DropdownSelect
                  classes={{ root: classes.dropdownSelect }}
                  label={`Choose where to apply tag(s)`}
                  required
                  options={automationDocOptions}
                  value={automationDocOptions.filter((doc) =>
                    (currentAutomation.actions[0].config as AddTagsActionConfig).targetDocs.value.includes(
                      doc.value as AutomationDocType
                    )
                  )}
                  onChange={(value) => {
                    setCurrentAutomation((prev) => ({
                      ...prev,
                      actions: [
                        {
                          ...prev.actions[0],
                          config: {
                            ...prev.actions[0].config,
                            targetDocs: { value: value.map((v) => v.value) }
                          }
                        }
                      ]
                    }))
                  }}
                  variant="multi"
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.confirm}
          variant={'contained'}
          disabled={isLoading || automationIncomplete(currentAutomation)}
          data-testid="automation-option-dialog-confirm-button"
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      onConfirm(currentAutomation)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }
}

export function getDefaultActionConfig(value: AutomationActionType): AutomationAction {
  switch (value) {
    case 'update-deal-stage':
      return {
        type: value,
        config: {
          input: ['quote', 'deal'],
          targetStage: { value: '' },
          targetPipeline: { value: 'sales-pipeline' }
        }
      }
    case 'add-tags':
      return {
        type: value,
        config: {
          input: ['quote', 'deal', 'company', 'contact'],
          tags: {
            value: []
          },
          targetDocs: {
            value: []
          }
        }
      }
  }
}

export function automationIncomplete(currentAutomation: AutomationOption): boolean {
  // check through the automation and enxure it has a trigger with filled out config, and at least one action, all with filled out config
  if (!currentAutomation.trigger || !currentAutomation.trigger.config) {
    return true
  }
  if (currentAutomation.actions.length === 0 || !currentAutomation.actions[0].config) {
    return true
  }

  if (
    currentAutomation.trigger.type === 'quote-status-changed' ||
    currentAutomation.trigger.type === 'invoice-status-changed'
  ) {
    if (!currentAutomation.trigger.config.targetStatus || !currentAutomation.trigger.config.targetStatus.value) {
      return true
    }
    if (
      !currentAutomation.trigger.config.quoteTypes ||
      !currentAutomation.trigger.config.quoteTypes.value ||
      currentAutomation.trigger.config.quoteTypes.value.length === 0
    ) {
      return true
    }
  }

  if (currentAutomation.actions[0].type === 'update-deal-stage') {
    const currentActionConfig = currentAutomation.actions[0].config as UpdateDealStageActionConfig
    if (
      !currentActionConfig.targetStage ||
      !currentActionConfig.targetStage.value ||
      !currentActionConfig.targetPipeline ||
      !currentActionConfig.targetPipeline.value
    ) {
      return true
    }
  }

  if (currentAutomation.actions[0].type === 'add-tags') {
    const currentActionConfig = currentAutomation.actions[0].config as AddTagsActionConfig
    if (!currentActionConfig.tags || !currentActionConfig.tags.value || currentActionConfig.tags.value.length === 0) {
      return true
    }
    if (
      !currentActionConfig.targetDocs ||
      !currentActionConfig.targetDocs.value ||
      currentActionConfig.targetDocs.value.length === 0
    ) {
      return true
    }
  }
}

export default AutomationOptionDialog
